import AddCircle from '@mui/icons-material/AddCircle';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Radio from '@mui/material/Radio';
import React from 'react';
import { CHECKBOX, FREE_TEXT, MATRICE, RADIO, TABLE } from '../../constants';
import { BlockProps } from '../../models/global';
import { CodeInterface } from '../../models/state';
import TextField from '../FormComponents/TextField';
import Matrice from './Matrice';
import Table from './Table';

interface FieldProps {
  chapterId: string;
  sectionId: string;
  block: BlockProps;
  handleOptions: (val: string, chapterId: string, sectionId: string, blockId: string, optionId: string) => void;
  addOption: (chapterId: string, sectionId: string, blockId: string) => void;
  removeOption: (chapterId: string, blockId: string, sectionId: string, i: number) => void;
  handleColumnName: (
    value: string,
    chapterId: string,
    sectionId: string,
    blockId: string,
    colId: string,
    type: 'table' | 'matrice',
  ) => void;
  handleMatrixRowCode: (
    value: CodeInterface,
    chapterId: string,
    sectionId: string,
    blockId: string,
    rowId: string,
  ) => void;
  handleMatrixRowName: (value: string, chapterId: string, sectionId: string, blockId: string, rowId: string) => void;
  addColumn: (
    chapterId: string,
    sectionId: string,
    blockId: string,
    columnType: string,
    type: 'table' | 'matrice',
  ) => void;
  addRow: (chapterId: string, sectionId: string, blockId: string) => void;
  removeColumn: (
    chapterId: string,
    sectionId: string,
    blockId: string,
    colId: string,
    type: 'table' | 'matrice',
  ) => void;
  removeRow: (chapterId: string, sectionId: string, blockId: string, rowId: string) => void;
  handleOtherOption: (chapterId: string, sectionId: string, blockId: string) => void;
  swapOptions: (
    chapterId: string,
    sectionId: string,
    blockId: string,
    optionIndex: number,
    direction: 'prev' | 'next',
  ) => void;
  changeColumnTypes: (chapterId: string, sectionId: string, blockId: string, columnType: string) => void;
  invalidFields: string[];
  language: string;
  readonly?: boolean;
  filteredCodes: CodeInterface[];
}

const Field = React.memo(
  ({
    chapterId,
    sectionId,
    block,
    handleOptions,
    addOption,
    removeOption,
    invalidFields,
    handleColumnName,
    handleMatrixRowName,
    handleMatrixRowCode,
    addColumn,
    addRow,
    removeColumn,
    removeRow,
    language,
    handleOtherOption,
    swapOptions,
    changeColumnTypes,
    readonly,
    filteredCodes,
  }: FieldProps) => {
    const otherOption = (
      <Box>
        {block.hasOtherOption && (
          <TextField
            id='Other'
            label='Other...'
            variant='standard'
            disabled
            sx={{
              ml: 5,
              mt: 1,
              width: '30%',
            }}
          />
        )}
        <Box
          sx={{
            ml: 5,
            mt: !block.hasOtherOption ? 7 : 0,
          }}
        >
          <Typography display='inline'>Include other option?</Typography>{' '}
          <Tooltip title='Other option' sx={{ pl: 0 }}>
            <Checkbox
              onClick={() => handleOtherOption(chapterId, sectionId, block.id)}
              checked={block.hasOtherOption}
            />
          </Tooltip>
        </Box>
      </Box>
    );

    switch (block.fieldType) {
      case CHECKBOX:
        return (
          <>
            {block.options.map((option, index) => (
              <Box key={index}>
                <Box className='options-block'>
                  <Checkbox disabled sx={{ m: 0 }} />
                  <TextField
                    label='Option'
                    variant='standard'
                    sx={{ width: '50%' }}
                    value={language === 'English' ? option.name : `${option[`name${language}`] || ''}`}
                    onChange={e => handleOptions(e.target.value, chapterId, sectionId, block.id, option.id)}
                    error={invalidFields.includes(`OPTION_${index}_${block.id}`) && language === 'English'}
                    required
                  />
                  {index === 0 && (
                    <AddCircle htmlColor='green' onClick={() => addOption(chapterId, sectionId, block.id)} />
                  )}
                  {index > 0 && (
                    <RemoveCircleIcon
                      htmlColor='red'
                      onClick={() => removeOption(chapterId, block.id, sectionId, index)}
                    />
                  )}

                  <>
                    {index !== block.options.length - 1 && (
                      <Tooltip title='Swap with the next one'>
                        <ArrowDownwardIcon
                          fontSize='small'
                          onClick={() => swapOptions(chapterId, sectionId, block.id, index, 'next')}
                        />
                      </Tooltip>
                    )}
                    {index !== 0 && (
                      <Tooltip title='Swap with the previous one'>
                        <ArrowUpwardIcon
                          fontSize='small'
                          onClick={() => swapOptions(chapterId, sectionId, block.id, index, 'prev')}
                        />
                      </Tooltip>
                    )}
                  </>
                </Box>
                {language !== 'English' && (
                  <Typography className='english-value' sx={{ pl: 4 }}>
                    English: {option.name || 'N/A'}
                  </Typography>
                )}
              </Box>
            ))}
            {otherOption}
          </>
        );
      case RADIO:
        return (
          <>
            {block.options.map((option, index) => (
              <Box key={index}>
                <Box className='options-block'>
                  <Radio disabled />
                  <TextField
                    label='Option'
                    variant='standard'
                    sx={{ width: '50%' }}
                    value={language === 'English' ? option.name : `${option[`name${language}`] || ''}`}
                    onChange={e => handleOptions(e.target.value, chapterId, sectionId, block.id, option.id)}
                    error={invalidFields.includes(`OPTION_${index}_${block.id}`) && language === 'English'}
                    required
                  />
                  {index === 0 && (
                    <AddCircle htmlColor='green' onClick={() => addOption(chapterId, sectionId, block.id)} />
                  )}
                  {index > 0 && (
                    <RemoveCircleIcon
                      htmlColor='red'
                      onClick={() => removeOption(chapterId, block.id, sectionId, index)}
                    />
                  )}
                  <>
                    {index !== block.options.length - 1 && (
                      <Tooltip title='Swap with the next one'>
                        <ArrowDownwardIcon
                          fontSize='small'
                          onClick={() => swapOptions(chapterId, sectionId, block.id, index, 'next')}
                        />
                      </Tooltip>
                    )}
                    {index !== 0 && (
                      <Tooltip title='Swap with the previous one'>
                        <ArrowUpwardIcon
                          fontSize='small'
                          onClick={() => swapOptions(chapterId, sectionId, block.id, index, 'prev')}
                        />
                      </Tooltip>
                    )}
                  </>
                </Box>
                {language !== 'English' && (
                  <Typography className='english-value' sx={{ pl: 4 }}>
                    English: {option.name || 'N/A'}
                  </Typography>
                )}
              </Box>
            ))}
            {otherOption}
          </>
        );
      case FREE_TEXT:
        return <TextField id='answer' label='Answer...' variant='standard' disabled sx={{ ml: 2 }} />;
      case TABLE:
        return (
          <Table
            handleTableColumnName={(val: string, colId: string) =>
              handleColumnName(val, chapterId, sectionId, block.id, colId, 'table')
            }
            addColumn={(columnType: string) => addColumn(chapterId, sectionId, block.id, columnType, 'table')}
            removeColumn={(colId: string) => removeColumn(chapterId, sectionId, block.id, colId, 'table')}
            table={block.table}
            language={language}
            invalidFields={invalidFields}
            readonly={readonly}
            blockId={block.id}
          />
        );
      case MATRICE:
        return (
          <Matrice
            handleTableColumnName={(val: string, colId: string) =>
              handleColumnName(val, chapterId, sectionId, block.id, colId, 'matrice')
            }
            handleMatrixRowCode={(val: CodeInterface, rowId: string) =>
              handleMatrixRowCode(val, chapterId, sectionId, block.id, rowId)
            }
            handleMatrixRowName={(val: string, rowId: string) =>
              handleMatrixRowName(val, chapterId, sectionId, block.id, rowId)
            }
            addColumn={(columnType: string) => addColumn(chapterId, sectionId, block.id, columnType, 'matrice')}
            addRow={() => addRow(chapterId, sectionId, block.id)}
            removeColumn={(colId: string) => removeColumn(chapterId, sectionId, block.id, colId, 'matrice')}
            removeRow={(rowId: string) => removeRow(chapterId, sectionId, block.id, rowId)}
            changeColumnTypes={(columnType: string) => changeColumnTypes(chapterId, sectionId, block.id, columnType)}
            matrice={block.matrice}
            language={language}
            invalidFields={invalidFields}
            readonly={readonly}
            blockId={block.id}
            filteredCodes={filteredCodes}
          />
        );
    }
  },
);

Field.displayName = 'Field';

export default Field;
